import { useState, useEffect, useCallback } from "react";
import { AlertCircle, Sparkles, X, ChevronDown, ChevronUp, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * DuplicateBanner
 *
 * Shows a yellow banner when the duplicate-detection endpoint returns groups.
 *
 * Props:
 *   - fetchFn: async () => { groups, ...extras } — must return { groups: [{key, name, items, count}] }
 *   - mergeFn: async (keys: string[]) => { merged_groups, ... }
 *   - title: string label e.g. "duplikater i beholdningen"
 *   - extraNotice: optional ReactNode to render below the groups (used for in-pantry cross-check)
 *   - onMerged: callback after successful merge
 *   - testIdPrefix: e.g. "pantry-dupe" or "shopping-dupe"
 */
export default function DuplicateBanner({ fetchFn, mergeFn, aiFetchFn, rejectFn, title, extraNotice, onMerged, onRemoveInPantry, testIdPrefix = "dupe" }) {
  const [groups, setGroups] = useState([]);
  const [aiGroups, setAiGroups] = useState([]);
  const [extra, setExtra] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [confirm, setConfirm] = useState(null); // {group, kind: "ai"|"normal", canonicalName}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFn();
      const data = res?.data ?? res;
      setGroups(data?.groups || []);
      setExtra(data || null);
    } catch (err) {
      // silent
      console.warn("Duplicate fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => { refresh(); }, [refresh]);

  // Expose refresh externally so parent can re-check after add/edit
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener(`${testIdPrefix}-refresh`, handler);
    return () => window.removeEventListener(`${testIdPrefix}-refresh`, handler);
  }, [refresh, testIdPrefix]);

  const runAiCheck = async () => {
    if (!aiFetchFn || aiLoading) return;
    setAiLoading(true);
    try {
      const res = await aiFetchFn();
      const data = res?.data ?? res;
      const gs = data?.groups || [];
      setAiGroups(gs);
      if (gs.length === 0) {
        toast.success("AI fant ingen kryss-merke-duplikater");
      } else {
        toast.success(`AI fant ${gs.length} mulige merke-duplikater`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI-sjekk feilet");
    } finally {
      setAiLoading(false);
    }
  };

  const mergeAiGroup = async (group) => {
    setMerging(true);
    try {
      const itemIds = (group.items || []).map((i) => i.id);
      const nameOverride = confirm?.canonicalName || group.name;
      const categoryOverride = confirm?.categoryOverride || null;
      const res = await mergeFn(
        null,
        [itemIds],
        { "explicit:0": nameOverride },
        categoryOverride ? { "explicit:0": categoryOverride } : null,
      );
      const data = res?.data ?? res;
      toast.success(`Slått sammen som «${nameOverride}» · fjernet ${data?.deleted_items || 0} duplikater`);
      setAiGroups((prev) => prev.filter((g) => g.key !== group.key));
      if (onMerged) onMerged();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved sammenslåing");
    } finally {
      setMerging(false);
      setConfirm(null);
    }
  };

  // User-driven AI-learning: register that this duplicate pair should NOT be
  // suggested again. Removes the group from view immediately.
  const rejectGroup = async (group, kind) => {
    if (!rejectFn) {
      // No-op: fall back to silent dismissal in-state if the page didn't
      // wire a rejectFn yet
      if (kind === "ai") setAiGroups((prev) => prev.filter((g) => g.key !== group.key));
      else setGroups((prev) => prev.filter((g) => g.key !== group.key));
      setConfirm(null);
      return;
    }
    try {
      const names = (group.items || []).map((i) => i.name).filter(Boolean);
      await rejectFn(names);
      toast.success(`AI vil ikke lenger foreslå «${names.slice(0, 2).join("» og «")}»`);
      if (kind === "ai") setAiGroups((prev) => prev.filter((g) => g.key !== group.key));
      else setGroups((prev) => prev.filter((g) => g.key !== group.key));
      setConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke registrere avvising");
    }
  };

  const mergeAll = async () => {
    if (merging || groups.length === 0) return;
    setMerging(true);
    try {
      const res = await mergeFn(groups.map((g) => g.key));
      const data = res?.data ?? res;
      const removed = data?.deleted_items ?? data?.removed_items ?? 0;
      toast.success(`Slo sammen ${data?.merged_groups || 0} grupper · fjernet ${removed} duplikater`);
      setGroups([]);
      if (onMerged) onMerged();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved sammenslåing");
    } finally {
      setMerging(false);
    }
  };

  const mergeOne = async (key) => {
    setMerging(true);
    try {
      const overrideName = confirm?.canonicalName;
      const categoryOverride = confirm?.categoryOverride || null;
      const res = await mergeFn(
        [key],
        null,
        overrideName ? { [key]: overrideName } : undefined,
        categoryOverride ? { [key]: categoryOverride } : null,
      );
      const data = res?.data ?? res;
      const removed = data?.deleted_items ?? data?.removed_items ?? 0;
      toast.success(`Slått sammen · fjernet ${removed} duplikater`);
      if (onMerged) onMerged();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved sammenslåing");
    } finally {
      setMerging(false);
      setConfirm(null);
    }
  };

  const inPantryCount = (extra?.in_pantry || []).length;
  if (dismissed || loading) return null;
  const hasContent = groups.length > 0 || aiGroups.length > 0 || inPantryCount > 0;
  // Always render the banner if AI check is available — even with no detected
  // duplicates — so the user can trigger the AI cross-brand check.
  if (!hasContent && !aiFetchFn) return null;

  return (
    <div className={`rounded-xl border ${hasContent ? "border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50" : "border-stone-200 bg-stone-50"} p-3 mb-3`} data-testid={`${testIdPrefix}-banner`}>
      <div className="flex items-start gap-2">
        <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${hasContent ? "text-amber-600" : "text-stone-400"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className={`text-sm font-semibold ${hasContent ? "text-amber-900" : "text-stone-700"}`}>
              {groups.length > 0 && `${groups.length} ${title}`}
              {groups.length > 0 && (aiGroups.length > 0 || inPantryCount > 0) && " · "}
              {aiGroups.length > 0 && `${aiGroups.length} AI-foreslåtte merke-duplikater`}
              {aiGroups.length > 0 && inPantryCount > 0 && " · "}
              {inPantryCount > 0 && `${inPantryCount} varer finnes alt i beholdning`}
              {!hasContent && "Beholdningen ser ryddig ut"}
            </div>
            <div className="flex items-center gap-1">
              {aiFetchFn && (
                <button
                  onClick={runAiCheck}
                  disabled={aiLoading}
                  className="inline-flex items-center gap-1 rounded-md bg-violet-600 hover:bg-violet-700 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
                  data-testid={`${testIdPrefix}-ai-check`}
                  title="Be AI om å finne merke-duplikater (f.eks. Coop paranøtter ↔ Rema paranøtter)"
                >
                  <Sparkles className="h-3 w-3" /> {aiLoading ? "Sjekker…" : "Sjekk med AI"}
                </button>
              )}
              {groups.length > 0 && (
                <button
                  onClick={mergeAll}
                  disabled={merging}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-600 hover:bg-amber-700 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
                  data-testid={`${testIdPrefix}-merge-all`}
                >
                  <Sparkles className="h-3 w-3" /> {merging ? "Slår sammen…" : "Slå sammen alle"}
                </button>
              )}
              <button
                onClick={() => setCollapsed((c) => !c)}
                className="p-1 rounded hover:bg-amber-100 text-amber-700"
                data-testid={`${testIdPrefix}-toggle`}
                title={collapsed ? "Vis detaljer" : "Skjul detaljer"}
              >
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="p-1 rounded hover:bg-amber-100 text-amber-700"
                data-testid={`${testIdPrefix}-dismiss`}
                title="Skjul varselet"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!collapsed && hasContent && (
            <div className="mt-2 space-y-2">
              {/* AI-detected cross-brand groups (purple accent) */}
              {aiGroups.map((g) => (
                <div key={g.key} className="rounded-md bg-violet-50/70 border border-violet-200 px-2 py-1.5" data-testid={`${testIdPrefix}-ai-group-${g.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-violet-900 truncate flex-1 min-w-0">
                      <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5 text-violet-600" />
                      <strong>{g.name}</strong>
                      <span className="text-violet-700/80"> · {g.count} merker:</span>
                      <span className="text-violet-700/70 ml-1">
                        {(g.items || []).map((i) => i.name).join(", ")}
                      </span>
                      {g.reason && (
                        <span className="block text-[10px] text-violet-600/70 italic mt-0.5">{g.reason}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setConfirm({ group: g, kind: "ai", canonicalName: g.name })}
                        disabled={merging}
                        className="text-[10px] uppercase tracking-wider font-bold text-violet-700 hover:text-violet-900 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-ai-merge-${g.key}`}
                      >
                        Slå sammen
                      </button>
                      {rejectFn && (
                        <button
                          onClick={() => rejectGroup(g, "ai")}
                          disabled={merging}
                          className="p-1 rounded hover:bg-violet-100 text-violet-500 hover:text-violet-800 disabled:opacity-50"
                          data-testid={`${testIdPrefix}-ai-reject-${g.key}`}
                          title="Avvis — desse er ikkje like, AI vil ikkje foreslå igjen"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Deterministic groups (yellow accent) */}
              {groups.map((g) => (
                <div key={g.key} className="rounded-md bg-white/70 border border-amber-200 px-2 py-1.5" data-testid={`${testIdPrefix}-group-${g.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-amber-900 truncate">
                      <strong>{g.name}</strong>
                      <span className="text-amber-700/80"> · {g.count} oppføringer:</span>
                      <span className="text-amber-700/70 ml-1 truncate">
                        {(g.items || []).map((i) => i.name).slice(0, 4).join(", ")}
                        {g.count > 4 ? "…" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setConfirm({ group: g, kind: "normal", canonicalName: g.name })}
                        disabled={merging}
                        className="text-[10px] uppercase tracking-wider font-bold text-amber-700 hover:text-amber-900 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-merge-${g.key}`}
                      >
                        Slå sammen
                      </button>
                      {rejectFn && (
                        <button
                          onClick={() => rejectGroup(g, "normal")}
                          disabled={merging}
                          className="p-1 rounded hover:bg-amber-100 text-amber-500 hover:text-amber-800 disabled:opacity-50"
                          data-testid={`${testIdPrefix}-reject-${g.key}`}
                          title="Avvis — desse er ikkje like, AI vil ikkje foreslå igjen"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {extraNotice ? (
                <div className="mt-1">{extraNotice}</div>
              ) : (extra?.in_pantry?.length > 0 && (
                <div className="rounded-md bg-white/70 border border-amber-200 px-2 py-1.5">
                  <div className="text-[11px] text-amber-800 font-semibold mb-1">Disse varene finnes alt i beholdning:</div>
                  <div className="flex flex-wrap gap-1">
                    {extra.in_pantry.slice(0, 12).map((p) => (
                      <span key={p.list_item_id} className="inline-flex items-stretch text-[10px] bg-white border border-amber-200 rounded-full overflow-hidden" data-testid={`${testIdPrefix}-pantry-chip-${p.list_item_id}`}>
                        <span className="px-2 py-0.5 text-amber-900">
                          {p.list_item_name} ({p.pantry_amount} {p.pantry_unit})
                        </span>
                        {onRemoveInPantry && (
                          <button
                            onClick={async () => {
                              try {
                                await onRemoveInPantry(p);
                                setExtra((prev) => prev ? { ...prev, in_pantry: (prev.in_pantry || []).filter((x) => x.list_item_id !== p.list_item_id) } : prev);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Kunne ikke fjerne");
                              }
                            }}
                            className="px-1.5 border-l border-amber-200 bg-red-50 text-red-600 hover:bg-red-100 flex items-center"
                            title={`Fjern '${p.list_item_name}' fra handlelisten`}
                            data-testid={`${testIdPrefix}-pantry-remove-${p.list_item_id}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    {extra.in_pantry.length > 12 && (
                      <span className="text-[10px] text-amber-700">+{extra.in_pantry.length - 12} til</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirm-merge dialog: shows both items + lets user edit canonical name */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {confirm?.kind === "ai" ? <Sparkles className="h-5 w-5 text-violet-600" /> : <AlertCircle className="h-5 w-5 text-amber-600" />}
              Bekreft sammenslåing
            </DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              Sjekk at varene faktisk er like før du slår dem sammen.
            </DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="space-y-3">
              <div className="space-y-2" data-testid={`${testIdPrefix}-confirm-items`}>
                {(confirm.group.items || []).map((it, i) => {
                  const entries = it.locations || [{ amount: it.amount, unit: it.unit, location_id: it.location_id }];
                  return (
                    <div key={it.id || i} className="rounded-md border border-stone-200 bg-white px-3 py-2">
                      <div className="text-sm font-semibold text-stone-800">{it.name || "Uten navn"}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        {entries.map((e, j) => (
                          <span key={`${it.id || "x"}-loc-${e.id || e.location_id || j}`} className="inline-flex items-center mr-3">
                            <span className="font-medium text-stone-600">{e.amount || "?"} {e.unit || "stk"}</span>
                            {e.expiry_date && <span className="ml-1.5 text-amber-700">· Utløp {e.expiry_date}</span>}
                            {e.note && <span className="ml-1.5 italic text-stone-400">· {e.note}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {confirm.group.reason && (
                <div className="text-[11px] text-violet-700 italic px-2">
                  AI: {confirm.group.reason}
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1 mb-1">
                  <Pencil className="h-3 w-3" /> Nytt felles navn
                </label>
                <input
                  type="text"
                  value={confirm.canonicalName}
                  onChange={(e) => setConfirm((c) => c ? { ...c, canonicalName: e.target.value } : c)}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:border-navy-800 focus:outline-none"
                  data-testid={`${testIdPrefix}-confirm-name`}
                />
                <p className="text-[10px] text-stone-400 mt-1">Forslag fra AI. Du kan endre dette navnet.</p>
              </div>
              {/* Kategori-val: vis radio-knappar når varene har ulik kategori */}
              {(() => {
                const cats = [...new Set((confirm.group.items || []).map((i) => i.category).filter(Boolean))];
                if (cats.length < 2) return null;
                const selected = confirm.categoryOverride || cats[0];
                return (
                  <div data-testid={`${testIdPrefix}-confirm-category-picker`}>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1 block">
                      Kategoriane er ulike — vel èin
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {cats.map((c) => (
                        <label key={c} className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs cursor-pointer ${selected === c ? "border-navy-800 bg-navy-50 text-navy-900 font-semibold" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}>
                          <input
                            type="radio"
                            name={`cat-pick-${confirm.group.key}`}
                            checked={selected === c}
                            onChange={() => setConfirm((cc) => cc ? { ...cc, categoryOverride: c } : cc)}
                            className="accent-navy-800"
                            data-testid={`${testIdPrefix}-confirm-cat-${c}`}
                          />
                          {c}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
                <button
                  onClick={() => rejectGroup(confirm.group, confirm.kind)}
                  disabled={merging}
                  className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-700"
                  data-testid={`${testIdPrefix}-confirm-reject`}
                  title="AI vil ikkje foreslå desse igjen"
                >
                  <X className="h-3 w-3" /> Avvis (ikkje like)
                </button>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => setConfirm(null)}
                    disabled={merging}
                    className="rounded-md border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-50"
                    data-testid={`${testIdPrefix}-confirm-cancel`}
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={() => {
                      if (confirm.kind === "ai") mergeAiGroup(confirm.group);
                      else mergeOne(confirm.group.key);
                    }}
                    disabled={merging || !confirm.canonicalName.trim()}
                    className="inline-flex items-center gap-1.5 rounded-md bg-navy-800 hover:bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    data-testid={`${testIdPrefix}-confirm-merge`}
                  >
                    <Check className="h-3 w-3" /> {merging ? "Slår sammen..." : "Slå sammen"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
